import React from "react";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  useMediaQuery,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import DateRangeSelector from "components/DateRangeSelector";
import { useDispatch, useSelector } from "react-redux";
import { loadStatements } from "redux/statements/statementsFunctions";
import { RootState } from "redux/rootReducer";
import * as StatementsTypes from "../types/Statements";
import { asEur } from "utility/numbers";
import { dateToString } from "utility/date";
import { Cell } from "react-table";
import NoTransactions from "components/NoTransactions";
import { generateFilename } from "../utility/files";
import PortfolioName from "../components/PortfolioName";
import ExportExcel from "../components/ExportExcel";
import Table from "../components/Table";

const Statements = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );

  const statementsData = useSelector(
    (state: RootState) => state.statements.data ?? []
  );

  const statementsLoading = useSelector(
    (state: RootState) => state.statements.loading
  );

  const startDate = useSelector((state: RootState) => state.user.startDate);

  const endDate = useSelector((state: RootState) => state.user.reportDate);

  React.useEffect(() => {
    if (reportControlId && startDate && endDate) {
      dispatch(loadStatements(reportControlId, startDate, endDate));
    }
  }, [dispatch, reportControlId, startDate, endDate]);

  return (
    <Grid
      container
      justifyContent={matchesMediaQuery ? "center" : "flex-start"}
    >
      <Grid item container md={10} sm={12}>
        {matchesMediaQuery ? (
          <Grid item xs={9}>
            <h1 style={{ color: "#003366" }}>{t("statements")}</h1>
          </Grid>
        ) : (
          <></>
        )}
        <Grid container item md={3} sm={12} alignContent="center">
          <DateRangeSelector />
        </Grid>
      </Grid>
      <Grid item container md={10} style={{ padding: "1em 0" }}>
        {statementsData.length > 0 ? (
          statementsData.map(
            (
              data: StatementsTypes.PortfolioStatements,
              portfolioIndex: number
            ) => {
              const isExpandedDefault = portfolioIndex === 0 ? true : false;
              const startBalance = data.Statements[0].StartBalance;
              const saldoArray: number[] = [];
              const endSaldo = data.Statements.reduce((prev, curr) => {
                const saldo = prev + curr.TotalAmount;
                saldoArray.push(saldo);
                return saldo;
              }, startBalance);

              const newData = data.Statements.map(
                (statement, statementIndex) => ({
                  ...statement,
                  Saldo: saldoArray[statementIndex],
                  Withdraw:
                    Math.sign(statement.TotalAmount) === 1
                      ? 0
                      : statement.TotalAmount,
                  Deposit:
                    Math.sign(statement.TotalAmount) === 1
                      ? statement.TotalAmount
                      : 0,
                })
              );

              const tableColumns = [
                {
                  Header: t("date_of_payment"),
                  accessor: "DateOfPayment",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("transaction_date"),
                  accessor: "TransactionDate",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("description"),
                  accessor: "Description",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                },
                {
                  Header: t("withdraw"),
                  accessor: "Withdraw",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<StatementsTypes.Statement, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: (
                    <>
                      <div>{t("start_saldo")}</div>
                      <div>{t("end_saldo")}</div>
                    </>
                  ),
                },
                {
                  Header: t("deposit"),
                  accessor: "Deposit",
                  paddingRight: "10px",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<StatementsTypes.Statement, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                },
                {
                  Header: t("saldo"),
                  accessor: "Saldo",
                  width: "fit-content",
                  fontFamily: "Lato light",
                  Cell: (cell: Cell<StatementsTypes.Statement, number>) =>
                    asEur(cell.value, 2),
                  textAlign: "right",
                  Footer: (
                    <>
                      <div>{asEur(startBalance, 2)}</div>
                      <div>{asEur(endSaldo, 2)}</div>
                    </>
                  ),
                },
              ];

              const tableData = newData.map((statement) => ({
                ...statement,
                DateOfPayment: dateToString(statement.DateOfPayment),
                TransactionDate: dateToString(statement.TransactionDate),
              }));

              const excelHeaders = [
                tableColumns.map((column) => column.Header),
              ];

              const excelData = newData.map(
                (statement: {
                  Saldo: number;
                  Withdraw: number | string;
                  Deposit: number;
                  RiviNumero: number;
                  CurrencyIdName: string;
                  DateOfPayment: Date;
                  Description: string;
                  TransactionDate: Date;
                  TotalAmount: number;
                  StartBalance: number;
                }) => ({
                  DateOfPayment: dateToString(statement.DateOfPayment),
                  TransactionDate: dateToString(statement.TransactionDate),
                  Description: statement.Description,
                  Withdraw: statement.Withdraw,
                  Deposit: statement.Deposit,
                  Saldo: statement.Saldo,
                })
              );

              const excelSums = [
                {
                  DateOfPayment: "",
                  TransactionDate: "",
                  Description: "",
                  Withdraw: t("start_saldo"),
                  Deposit: 0,
                  Saldo: startBalance,
                },
                {
                  DateOfPayment: "",
                  TransactionDate: "",
                  Description: "",
                  Withdraw: t("end_saldo"),
                  Deposit: 0,
                  Saldo: endSaldo,
                },
              ];
              excelSums.forEach((footer) => excelData.push(footer));

              return (
                <Grid
                  key={data.PortfolioId}
                  item
                  xs={12}
                  justifyContent="center"
                  style={{ borderBottom: "1px solid #D3741C" }}
                >
                  <Accordion
                    defaultExpanded={isExpandedDefault}
                    style={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      style={{ padding: 0 }}
                    >
                      <PortfolioName name={data.PortfolioName} />
                    </AccordionSummary>
                    <ExportExcel
                      data={excelData}
                      headers={excelHeaders}
                      filename={generateFilename(
                        t("statements"),
                        data.PortfolioName,
                        startDate,
                        endDate
                      )}
                      matchesMediaQuery={matchesMediaQuery}
                    />
                    <AccordionDetails
                      style={{
                        maxWidth: "inherit",
                        overflowY: "hidden",
                        padding: matchesMediaQuery
                          ? "8px 16px 16px"
                          : "8px 0 16px 0",
                      }}
                    >
                      <Table
                        columns={tableColumns}
                        data={tableData}
                        loading={statementsLoading}
                        hasFooter
                      />
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            }
          )
        ) : (
          <NoTransactions />
        )}
      </Grid>
    </Grid>
  );
};

export default React.memo(Statements);
